import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  ArrowPathIcon,
  FunnelIcon,
  XMarkIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import {
  auditService,
  AUDIT_TAGS,
  AUDIT_MODULES,
  AUDIT_ACTIONS
} from '../../services/system/audit';
import AuditEntryRow from './AuditEntryRow';
import { driversService } from '../../services/logistics/drivers';
import { clientsService } from '../../services/logistics/clients';

const QUICK_FILTERS = [
  { id: AUDIT_TAGS.ALL, key: 'audit.quick.all', fallback: 'All Activity' },
  { id: AUDIT_TAGS.FINANCIAL, key: 'audit.quick.financial', fallback: 'Financial' },
  { id: AUDIT_TAGS.DELETIONS, key: 'audit.quick.deletions', fallback: 'Deletions' },
  { id: AUDIT_TAGS.TRIP_CHANGES, key: 'audit.quick.trip_changes', fallback: 'Trip Changes' },
  { id: AUDIT_TAGS.DRIVER_CHANGES, key: 'audit.quick.driver_changes', fallback: 'Driver Changes' },
  { id: AUDIT_TAGS.CUSTOMER_CHANGES, key: 'audit.quick.customer_changes', fallback: 'Customer Changes' },
  { id: AUDIT_TAGS.DOCUMENTS, key: 'audit.quick.documents', fallback: 'Documents' },
  { id: AUDIT_TAGS.USER_ACCESS, key: 'audit.quick.user_access', fallback: 'User Access' }
];

const PAGE_SIZE = 50;

const AuditTrailPanel = () => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [entries, setEntries] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [clients, setClients] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [page, setPage] = useState(1);

  const [filters, setFilters] = useState({
    quickFilter: AUDIT_TAGS.ALL,
    dateFrom: '',
    dateTo: '',
    actorId: '',
    module: '',
    action: '',
    customerId: '',
    driverId: '',
    tripNumber: '',
    invoiceNumber: '',
    paymentNumber: '',
    search: ''
  });

  const load = useCallback(async () => {
    if (!currentCompany?.id) return;
    setLoading(true);
    setError(null);
    try {
      // Load drivers + customers alongside entries so we can resolve ids in
      // meta to human names and power the driver dropdown filter.
      const [data, driverList, clientList] = await Promise.all([
        auditService.fetchByCompany(currentCompany.id, 1000),
        driversService.getDrivers(currentCompany.id).catch(() => []),
        clientsService.getClients(currentCompany.id).catch(() => [])
      ]);
      setEntries(data);
      setDrivers(Array.isArray(driverList) ? driverList : []);
      setClients(Array.isArray(clientList) ? clientList : []);
    } catch (err) {
      console.error('Error loading audit entries', err);
      setError(err.message || 'Failed to load audit entries');
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id]);

  const driverNameById = useMemo(() => {
    const m = {};
    drivers.forEach(d => {
      m[d.id] = d.name || [d.firstName, d.lastName].filter(Boolean).join(' ').trim() || d.id;
    });
    return m;
  }, [drivers]);

  const customerNameById = useMemo(() => {
    const m = {};
    clients.forEach(c => { m[c.id] = c.name || c.companyName || c.id; });
    return m;
  }, [clients]);

  const sortedDriverOptions = useMemo(() => {
    return [...drivers]
      .map(d => ({ id: d.id, name: driverNameById[d.id] || d.id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [drivers, driverNameById]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => auditService.applyFilters(entries, filters),
    [entries, filters]
  );

  // Distinct actors for the actor dropdown.
  const actors = useMemo(() => {
    const map = new Map();
    entries.forEach(e => {
      if (e.actorId && !map.has(e.actorId)) {
        map.set(e.actorId, { id: e.actorId, name: e.actorName || e.actorEmail || e.actorId });
      }
    });
    return Array.from(map.values());
  }, [entries]);

  const setFilter = (key, value) => {
    setPage(1);
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearAdvanced = () => {
    setPage(1);
    setFilters(prev => ({
      ...prev,
      dateFrom: '', dateTo: '', actorId: '', module: '', action: '',
      customerId: '', driverId: '', tripNumber: '', invoiceNumber: '',
      paymentNumber: '', search: ''
    }));
  };

  const visible = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = filtered.length > visible.length;

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <ClipboardDocumentListIcon className="w-6 h-6 text-blue-600" />
            {t('settings.audit_trail', 'Audit Trail')}
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            {t('audit.subtitle', 'Read-only history of important actions across the system.')}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {t('common.refresh', 'Refresh')}
        </button>
      </div>

      {/* Quick filter chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        {QUICK_FILTERS.map(qf => {
          const active = filters.quickFilter === qf.id;
          return (
            <button
              key={qf.id}
              onClick={() => setFilter('quickFilter', qf.id)}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                active
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {t(qf.key, qf.fallback)}
            </button>
          );
        })}
      </div>

      {/* Advanced filters toggle */}
      <div className="mb-4">
        <button
          onClick={() => setShowAdvanced(v => !v)}
          className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
        >
          <FunnelIcon className="w-4 h-4" />
          {showAdvanced
            ? t('audit.hide_advanced', 'Hide advanced filters')
            : t('audit.show_advanced', 'Show advanced filters')}
        </button>
      </div>

      {showAdvanced && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('audit.filter.date_from', 'From')}
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={e => setFilter('dateFrom', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('audit.filter.date_to', 'To')}
            </label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={e => setFilter('dateTo', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('audit.filter.user', 'User')}
            </label>
            <select
              value={filters.actorId}
              onChange={e => setFilter('actorId', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
            >
              <option value="">{t('common.any', 'Any')}</option>
              {actors.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('audit.filter.module', 'Module')}
            </label>
            <select
              value={filters.module}
              onChange={e => setFilter('module', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
            >
              <option value="">{t('common.any', 'Any')}</option>
              {Object.values(AUDIT_MODULES).map(m => (
                <option key={m} value={m}>{t(`audit.module.${m}`, m)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('audit.filter.action_type', 'Action type')}
            </label>
            <select
              value={filters.action}
              onChange={e => setFilter('action', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
            >
              <option value="">{t('common.any', 'Any')}</option>
              {Object.values(AUDIT_ACTIONS).map(a => (
                <option key={a} value={a}>{t(`audit.action.${a}`, a)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('audit.filter.customer', 'Customer ID')}
            </label>
            <input
              type="text"
              value={filters.customerId}
              onChange={e => setFilter('customerId', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
              placeholder="customer id"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('audit.filter.driver', 'Driver')}
            </label>
            <select
              value={filters.driverId}
              onChange={e => setFilter('driverId', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
            >
              <option value="">{t('common.any', 'Any')}</option>
              {sortedDriverOptions.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('audit.filter.trip_number', 'Trip number')}
            </label>
            <input
              type="text"
              value={filters.tripNumber}
              onChange={e => setFilter('tripNumber', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('audit.filter.invoice_number', 'Invoice number')}
            </label>
            <input
              type="text"
              value={filters.invoiceNumber}
              onChange={e => setFilter('invoiceNumber', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('audit.filter.payment_number', 'Payment number')}
            </label>
            <input
              type="text"
              value={filters.paymentNumber}
              onChange={e => setFilter('paymentNumber', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('audit.filter.search', 'Free text search')}
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={e => setFilter('search', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
              placeholder={t('audit.filter.search_placeholder', 'Search any field…')}
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
            <button
              onClick={clearAdvanced}
              className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
              <XMarkIcon className="w-4 h-4" />
              {t('audit.clear_filters', 'Clear advanced filters')}
            </button>
          </div>
        </div>
      )}

      {/* Results summary */}
      <div className="text-xs text-gray-500 mb-2">
        {t('audit.showing', 'Showing')} {visible.length} {t('audit.of', 'of')} {filtered.length}
        {entries.length !== filtered.length && (
          <> ({t('audit.total_loaded', 'loaded')} {entries.length})</>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-3 text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-2 w-8"></th>
              <th className="px-3 py-2 text-start font-medium text-gray-600">{t('audit.col.when', 'When')}</th>
              <th className="px-3 py-2 text-start font-medium text-gray-600">{t('audit.col.action', 'Action')}</th>
              <th className="px-3 py-2 text-start font-medium text-gray-600">{t('audit.col.module', 'Module')}</th>
              <th className="px-3 py-2 text-start font-medium text-gray-600">{t('audit.col.record', 'Record')}</th>
              <th className="px-3 py-2 text-start font-medium text-gray-600">{t('audit.col.who', 'Who')}</th>
              <th className="px-3 py-2 text-start font-medium text-gray-600">{t('audit.col.tags', 'Tags')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && entries.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">{t('common.loading', 'Loading…')}</td></tr>
            )}
            {!loading && visible.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">{t('audit.empty', 'No audit entries match the current filters.')}</td></tr>
            )}
            {visible.map(e => (
              <AuditEntryRow
                key={e.id}
                entry={e}
                driverNameById={driverNameById}
                customerNameById={customerNameById}
              />
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="mt-3 text-center">
          <button
            onClick={() => setPage(p => p + 1)}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {t('audit.load_more', 'Load more')}
          </button>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-400 italic">
        {t('audit.append_only_note', 'Audit entries are read-only and append-only — they cannot be edited or deleted from the application.')}
      </div>
    </div>
  );
};

export default AuditTrailPanel;
